import { decamelizeKeys } from 'humps';
import requester from '@/lib/axios';
import { BusinessModelEnum, Course, CourseFormatEnum } from '@/models/Course';
import { BaseFilters, ListServiceFunc } from '@/models/Service';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import {
  bannerImgConverter,
  convertToFormData,
} from '@/utils/convertToFormData';
import { MULTIPART_FORM_DATA_HEADERS as headers } from '@/constants';

export type CourseBody = Omit<Course, 'id' | 'bannerImg'> & {
  bannerImg: File | string | null;
};

export async function addCourse(
  course: Partial<CourseBody>,
  bannerImg: string | File | null,
): Promise<Course> {
  const formData = new FormData();

  Object.entries(course).forEach(([key, value]) => {
    if (key === 'isActive' || key === 'bannerImg') return;

    if (value) {
      formData.set(key, value instanceof File ? value : value.toString());
    }
  });

  if (bannerImg) formData.set('bannerImg', bannerImg);

  formData.set(
    'course',
    new Blob([JSON.stringify(decamelizeKeys(course))], {
      type: 'application/json',
    }),
  );

  const { data } = await requester().post<Course>('courses/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      accept: 'application/json',
    },
  });

  return data;
}

export interface CourseBasesFilters extends BaseFilters {
  status?: VersioningStatusEnum;
}
export interface ListCourseBaseFilters {
  formats?: CourseFormatEnum[];
  businessModels?: BusinessModelEnum[];
  pageSize?: number;
  pageNumber?: number;
  search?: string;
  courseLineId?: number[];
  moduleId?: number[];
  coursePathStatus?: VersioningStatusEnum[];
  courseId?: number[];
}

export const listCoursesBases: ListServiceFunc<
  Course,
  ListCourseBaseFilters
> = async params => {
  const { data } = await requester().get('courses-bases/', {
    params: { ...params, courseId: params?.courseId?.toString() },
  });
  return data;
};

export async function deleteCourse(id: number) {
  await requester().delete(`courses-bases/${id}/`);
}

export const getCourseBase = async (id: number) => {
  const { data } = await requester().get<Course>(`courses-bases/${id}/`);

  return data;
};

export async function updateCourse(id: number, fields: Partial<Course>) {
  const formData = convertToFormData(fields, {
    bannerImg: bannerImgConverter,
  });

  const { data } = await requester().patch<Course>(
    `courses-bases/${id}/`,
    formData,
    {
      headers,
    },
  );

  return data;
}
